// libs
import { useMemo } from 'react';

// types
import { PostFragment } from '@/features/messages/graphql/fragments/generated/Post';
import { useGetPostsQuery } from '@/features/messages/graphql/queries/generated/Posts';
import { useChannelPostsSubscription } from '@/features/messages/graphql/subscriptions/generated/ChannelPosts';
import useNavigation from '@/hooks/useNavigation';
// hooks
import usePagination from '@/hooks/usePagination';
import { ChannelPostsSubscriptionOperation, OrderEnum, PostTypeEnum } from '@/types/schema';

import { UseMessagesParams } from './types';

const _identity = (post: PostFragment) => post;

export default function useMessages(params: UseMessagesParams) {
  const {
    channelIds,
    topic,
    parentId,
    types = [PostTypeEnum.Post],
    isPinned,
    pause,
    adaptMessage = _identity,
    showHiddenPosts,
    pauseSubscription,
    postedByIds,
    showAllComments,
  } = params;
  const navigation = useNavigation();
  const isCommunityPage = navigation.route.includes('community');

  const {
    data,
    error,
    refetch,
    fetchMore,
    isFetching,
    totalCount = 0,
  } = usePagination(
    useGetPostsQuery,
    {
      filters: { channelIds, types, parentId, isPinned, showHiddenPosts, postedByIds },
      sort: [{ order: OrderEnum.Desc, field: 'createdAt' }],
    },
    {
      edgeKey: 'posts',
      requestPolicy: 'cache-and-network',
      pause,
      pageSize: showAllComments ? -1 : isCommunityPage ? 15 : 40,
    },
  );

  useChannelPostsSubscription({
    variables: {
      input: {
        channelIds: topic || channelIds,
        types,
        parentId,
        operations: [
          ChannelPostsSubscriptionOperation.PostCreated,
          ChannelPostsSubscriptionOperation.PostUpdated,
          ChannelPostsSubscriptionOperation.PostDeleted,
          ChannelPostsSubscriptionOperation.PostReactionUpdated,
        ],
      },
    },
    pause: isPinned || pauseSubscription,
  });

  const messages = useMemo(() => {
    /**
     * TEMPORARY
     *
     * currently, we are filtering messages based on channelId
     * because when there is the operation of delete post we used to null the channelId field inside the cache
     * in-future we will have a status attribute inside the PostType fragment, which we will use in filtering
     */
    return data.reduce<PostFragment[]>((accumulator, message) => {
      if (message.channelId) {
        accumulator.push(adaptMessage(message));
      }
      return accumulator;
    }, []);
  }, [adaptMessage, data]);

  return {
    isFetching,
    error,
    data: messages,
    refetch,
    fetchMore,
    totalCount,
  };
}
